type Nullable<T> = T | undefined | null
type Optional<T> = T | undefined

type ConverterClass<Dto, Resource> = { new(d: Dto): Resource };
type UnaryFn<Arg, Output> = (arg: Arg) => Output

export function tryCall<Arg, Output>(
  fn: UnaryFn<Arg, Output>,
  value: Nullable<Arg>,
): Optional<Output> {
  return value === undefined || value === null ? undefined : fn(value);
}

export function tryNew<Resource, Dto>(
  Cls: ConverterClass<Dto, Resource>,
  dto: Nullable<Dto>,
): Optional<Resource> {
  return tryCall((newDto) => new Cls(newDto), dto);
}

interface TryNewDateOptions {
  keepInvalid?: boolean;
}

export function tryNewDate(
  d: Nullable<number | string | Date>,
  options?: TryNewDateOptions,
): Optional<Date> {
  if (d === undefined || d === null) return undefined;
  const date = new Date(d);
  const isValid = !Number.isNaN(date.getTime());

  return !isValid && !options?.keepInvalid
    ? undefined
    : date;
}

interface TryMapNewOptions {
  undefinedIfEmpty?: boolean;
}

export function emptyArray2Undefined<Dto>(dtos: Nullable<Dto[]>): Optional<Dto[]> {
  return !dtos?.length ? undefined : dtos;
}

export function tryMapNew<Resource, Dto>(
  Cls: ConverterClass<Dto, Resource>,
  dto: Nullable<Dto[]>,
  options?: TryMapNewOptions,
): Optional<Resource[]> {
  const array = options?.undefinedIfEmpty ? emptyArray2Undefined(dto) : dto;
  return array?.map((d) => new Cls(d));
}

export function optional<T>(value: Nullable<T>): Optional<T> {
  return value ?? undefined;
}
